import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { platform } from "../platform/react";

// What's in front of the user (the current idea): pages register the rhythms and recordings they
// show (useAssistantSubject); the assistant sends the latest few with each message, and the tools
// work on the latest (useCurrentIdeas). Registering doesn't re-render the pages; only what reads
// the ideas listens for changes.
const Registry = createContext(null), Version = createContext(0);

export function AssistantProvider({ children }) {
  const entries = useRef(new Map()), order = useRef(0), [version, setVersion] = useState(0), actionSets = useRef(new Map());
  const register = useCallback((key, latest) => {
    entries.current.set(key, { latest, order: ++order.current });
    setVersion((value) => value + 1);
    return () => {
      if (entries.current.get(key)?.latest !== latest) return;
      entries.current.delete(key);
      setVersion((value) => value + 1);
    };
  }, []);
  // The subjects shown now, the most recently shown first.
  const subjects = useCallback(() => [...entries.current.values()].sort((a, b) => b.order - a.order).map((entry) => entry.latest.current).filter(Boolean), []);
  // What the page can do, as the assistant may ask it to (useAssistantActions): each set by its key.
  const registerActions = useCallback((key, latest) => {
    actionSets.current.set(key, latest);
    return () => { if (actionSets.current.get(key) === latest) actionSets.current.delete(key); };
  }, []);
  const actions = useCallback(() => {
    const all = new Map();
    // Plugins' actions (src/platform: api.action), then the page's.
    for (const action of platform().actions()) if (action?.name && action.run) all.set(action.name, action);
    for (const latest of actionSets.current.values()) for (const action of latest.current || []) if (action?.name && action.run) all.set(action.name, action);
    return [...all.values()];
  }, []);
  // An action run as the assistant asked: what it says it did, or why it couldn't. A page that's
  // still opening (the assistant opened it a moment ago) gets a few seconds to say what it can do.
  const runAction = useCallback(async (name, args = {}) => {
    let action = actions().find((item) => item.name === name);
    for (let waited = 0; !action && waited < 4000; waited += 200) {
      await new Promise((resolve) => setTimeout(resolve, 200));
      action = actions().find((item) => item.name === name);
    }
    if (!action) return { ok: false, text: `This page can't do “${name}” now.` };
    try { const said = await action.run(args || {}); return { ok: true, text: typeof said === "string" && said ? said : action.done || "Done." }; }
    catch (error) { return { ok: false, text: error?.message || "That didn't work." }; }
  }, [actions]);
  const registry = useMemo(() => ({ register, subjects, registerActions, actions, runAction }), [register, subjects, registerActions, actions, runAction]);
  return <Registry.Provider value={registry}><Version.Provider value={version}>{children}</Version.Provider></Registry.Provider>;
}

export const useAssistantRegistry = () => useContext(Registry);
export const useAssistantVersion = () => useContext(Version);
// What's shown now, the most recently shown first, kept up to date (docs/refactor-plan.md, Phase 2:
// the tools' current idea).
export function useCurrentIdeas() {
  const registry = useContext(Registry);
  useContext(Version);
  return registry ? registry.subjects() : [];
}

// What this component lets the assistant do while it's shown: [{ name ("score.add_bars"),
// description, args ({ name: { type, description, enum? } }), run(args) -> what it did (or throws) }].
// The assistant sees them with each message and asks the page to run one (page_action); plugins add
// theirs the same way (src/platform).
export function useAssistantActions(key, list) {
  const registry = useContext(Registry), latest = useRef(list);
  latest.current = list;
  useEffect(() => (registry?.registerActions && key ? registry.registerActions(key, latest) : undefined), [registry, key]);
}

// A rhythm ({ key, kind: "rhythm", title, score, tempo, where, mapId? }) or a recording ({ key,
// kind: "recording", title, seconds, where, file: () => Promise<Blob> }) the assistant can use while
// this component shows it; null for nothing. `where` says where it is ("open in Score").
export function useAssistantSubject(subject) {
  const registry = useContext(Registry), latest = useRef(subject);
  latest.current = subject;
  const key = subject?.key || null, title = subject?.title || "";
  useEffect(() => (registry && key ? registry.register(key, latest) : undefined), [registry, key, title]);
}
